import * as React from "react";
import { useCallback, useState } from "react";
import { styled } from "styled-components";

import { Form } from "affinityForm";
import { ExecuteButton } from "affinityForm/components/executeButton";
import { InputField, RangeField } from "affinityForm/fields";
import { Button, ButtonVariants } from "affinityUI/elements/button";
import { Card } from "affinityUI/elements/card";
import { Grid } from "affinityUI/elements/grid";
import { ButtonGroup, DataEntryGroup } from "affinityUI/form/components";
import { useHospitalConfiguration } from "components/pages/settings/context";
import { Restricted } from "lib/auth/components/restricted";
import { PERMISSIONS } from "lib/biot/roles/types";

const FieldsContainer = styled.div<{ $disabled: boolean }>`
  opacity: ${(props) => (props.$disabled ? 0.6 : 1)};
  pointer-events: ${(props) => (props.$disabled ? "none" : "auto")};
  transition: opacity 0.2s ease-in-out;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
`;

export const SettingsForm: React.FC<{
  onCancel: () => void;
}> = ({ onCancel: handleCancel }) => {
  const [isEditing, setIsEditing] = useState(false);

  const handleEditToggle = useCallback(() => {
    setIsEditing((prev) => !prev);
  }, []);
  const { region } = useHospitalConfiguration();
  const isUsa = region === "USA";

  return (
    <Card data-testid="settings-form">
      <Form>
        <HeaderContainer>
          <Restricted permits={[PERMISSIONS.HOSPITAL_CONFIG_CHANGE]}>
            <Button variant={ButtonVariants.SECONDARY} onClick={handleEditToggle} data-testid="edit-settings-button">
              {isEditing ? "Cancel Edit" : "Edit"}
            </Button>
          </Restricted>
        </HeaderContainer>
        <DataEntryGroup
          title="Normal ranges"
          description="Set up normal ranges for the following parameters"
          id="normal-ranges"
        >
          <FieldsContainer $disabled={!isEditing}>
            <RangeField name="wbcRange" disabled={!isEditing}>
              WBC (10⁹/L)
            </RangeField>
            {isUsa ? (
              <RangeField name="granulocyteRange" disabled={!isEditing}>
                Granulocytes (10⁹/L)
              </RangeField>
            ) : (
              <>
                <RangeField name="neutrophilRange" disabled={!isEditing}>
                  Neutrophils (10⁹/L)
                </RangeField>
                <RangeField name="haemoglobinRangeMale" disabled={!isEditing}>
                  Haemoglobin Male (g/L)
                </RangeField>
                <RangeField name="haemoglobinRangeFemale" disabled={!isEditing}>
                  Haemoglobin Female (g/L)
                </RangeField>
                <RangeField name="plateletRange" disabled={!isEditing}>
                  Platelets (10⁹/L)
                </RangeField>
              </>
            )}
          </FieldsContainer>
        </DataEntryGroup>
        <DataEntryGroup
          title="Confirmatory blood test threshold"
          description="Set a threshold where a patient may need to have a confirmatory blood test."
          id="confirmatory-test"
        >
          <Grid $cols={[1, 1]}>
            <FieldsContainer $disabled={!isEditing}>
              <InputField type="number" name="confirmatoryThreshold" disabled={!isEditing}>
                {isUsa ? "Granulocytes (10⁹/L)" : "Neutrophils (10⁹/L)"}
              </InputField>
            </FieldsContainer>
          </Grid>
        </DataEntryGroup>
        {isEditing && (
          <ButtonGroup>
            <Button variant={ButtonVariants.SECONDARY} onClick={handleCancel}>
              Cancel
            </Button>
            <ExecuteButton>Continue to confirmation</ExecuteButton>
          </ButtonGroup>
        )}
      </Form>
    </Card>
  );
};

export const EditButton: React.FC<{
  isEditing: boolean;
  onToggle: () => void;
}> = ({ isEditing, onToggle: handleToggle }) => {
  return (
    <Restricted permits={[PERMISSIONS.HOSPITAL_CONFIG_CHANGE]}>
      <Button variant={ButtonVariants.SECONDARY} onClick={handleToggle} data-testid="edit-settings-button">
        {isEditing ? "Cancel Edit" : "Edit"}
      </Button>
    </Restricted>
  );
};
